import { database, auth } from '../config/firebase';
import { ref, onValue, set, update, remove, push } from 'firebase/database';
import { Device, CommandType, DeviceCommand } from '../types';

function assertAuthenticated() {
  if (!auth.currentUser) {
    throw new Error('Usuário não autenticado');
  }
}

export const deviceService = {
  observeDevices: (callback: (devices: Device[]) => void) => {
    const devicesRef = ref(database, 'devices');
    
    return onValue(devicesRef, (snapshot) => {
      const data = snapshot.val();
      const devices: Device[] = data ? Object.values(data) : [];
      callback(devices);
    });
  },

  updateDevice: (device: Device) => {
    assertAuthenticated();
    const deviceRef = ref(database, `devices/${device.id}`);
    return set(deviceRef, device);
  },

  updateDeviceFields: (deviceId: string, fields: Partial<Device>) => {
    assertAuthenticated();
    const deviceRef = ref(database, `devices/${deviceId}`);
    return update(deviceRef, fields);
  },

  removeDevice: (deviceId: string) => {
    assertAuthenticated();
    const deviceRef = ref(database, `devices/${deviceId}`);
    return remove(deviceRef);
  },

  requestUpdate: async (deviceId: string) => {
    assertAuthenticated();
    const deviceRef = ref(database, `devices/${deviceId}`);
    return update(deviceRef, {
      updateRequested: true,
      updateRequestTime: Date.now()
    });
  },

  requestUninstall: async (deviceId: string, packageName: string, appName: string) => {
    assertAuthenticated();
    try {
      // Criar uma nova solicitação de desinstalação
      const uninstallRequestsRef = ref(database, `devices/${deviceId}/uninstallRequests`);
      const newRequestRef = push(uninstallRequestsRef);
      
      const uninstallRequest = {
        id: newRequestRef.key,
        packageName,
        appName,
        status: 'pending',
        requestedAt: Date.now(),
        updatedAt: Date.now()
      };

      await set(newRequestRef, uninstallRequest);
      
      return true;
    } catch (error) {
      console.error('Erro ao solicitar desinstalação:', error);
      throw error;
    }
  },

  cancelUninstallRequest: async (deviceId: string, requestId: string) => {
    assertAuthenticated();
    try {
      const requestRef = ref(database, `devices/${deviceId}/uninstallRequests/${requestId}`);
      await remove(requestRef);
      return true;
    } catch (error) {
      console.error('Erro ao cancelar solicitação de desinstalação:', error);
      throw error;
    }
  },

  updateDeviceInfo: async (deviceId: string, info: { customName?: string; companyName?: string; code?: string }) => {
    assertAuthenticated();
    try {
      // Remover campos undefined para evitar erros no Firebase
      const cleanInfo: Record<string, any> = {};
      
      if (info.customName !== undefined) {
        cleanInfo.customName = info.customName || null;
      }
      if (info.companyName !== undefined) {
        cleanInfo.companyName = info.companyName || null;
      }
      if (info.code !== undefined) {
        cleanInfo.code = info.code || null;
      }

      const deviceRef = ref(database, `devices/${deviceId}`);
      await update(deviceRef, cleanInfo);
      return true;
    } catch (error) {
      console.error('Erro ao atualizar informações do dispositivo:', error);
      throw error;
    }
  },

  toggleDeviceStatus: async (deviceId: string, isActive: boolean) => {
    assertAuthenticated();
    try {
      const deviceRef = ref(database, `devices/${deviceId}`);
      await update(deviceRef, { isActive });
      return true;
    } catch (error) {
      console.error('Erro ao alterar status do dispositivo:', error);
      throw error;
    }
  },

  sendCommand: async (
    deviceId: string,
    type: CommandType,
    sentBy?: { uid: string; email: string; displayName?: string },
    params?: Record<string, string>
  ) => {
    assertAuthenticated();
    try {
      const commandsRef = ref(database, `devices/${deviceId}/commands`);
      const newCommandRef = push(commandsRef);

      const command: DeviceCommand = {
        id: newCommandRef.key!,
        type,
        status: 'pending',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        ...(params && { params }),
        ...(sentBy && { sentBy })
      };

      await set(newCommandRef, command);
      return command;
    } catch (error) {
      console.error('Erro ao enviar comando:', error);
      throw error;
    }
  },

  observeCommands: (deviceId: string, callback: (commands: DeviceCommand[]) => void) => {
    const commandsRef = ref(database, `devices/${deviceId}/commands`);
    return onValue(commandsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        callback([]);
        return;
      }
      const commands: DeviceCommand[] = Object.values(data);
      // Ordenar por data de criação (mais recentes primeiro)
      commands.sort((a, b) => b.createdAt - a.createdAt);
      callback(commands);
    });
  }
}; 